import React, { useState, useEffect, useRef, useContext, useMemo } from "react";
import { useLocation } from "react-router-dom";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import { useTheme, alpha, keyframes } from "@mui/material";
import { useAuth } from "react-oidc-context";
import { useMutation } from "@tanstack/react-query";
import { updateSessionPlan, startSession, replyToSession, getSessionStatus, ClarificationField, PlanItemObject, GroundingRecord, InterrogationMemory, TranscriptSegment, isSessionComplete, updateSpeakerRegistry, LlmUsageSummary } from "./utils/backend-utils";
import { useLiveTranscription } from "./hooks/useLiveTranscription";
import AppContext from "./AppContext";

// Hooks
import { useSessionPolling, ChatMessage as SessionChatMessage } from "./hooks/useSessionPolling";
import { usePlanManagement, PlanItem } from "./hooks/usePlanManagement";

// Components
import { PlanSteps } from "./components/PlanSteps";
import { ChatMessages } from "./components/ChatMessages";
import { ChatInput } from "./components/ChatInput";
import { TypingIndicator } from "./components/TypingIndicator";
import { CollapsibleMessageBox } from "./components/CollapsibleMessageBox";
import { ClarificationForm } from "./components/ClarificationForm";
import { SessionDurationsCard } from "./components/SessionDurationsCard";
import { GroundingProceedDialog } from "./components/GroundingProceedDialog";
import { SessionHeader, SessionPhase } from "./components/SessionHeader";
import { PlanSidebar } from "./components/PlanSidebar";
import { InterrogationListenPanel } from "./components/InterrogationListenPanel";
import { InterrogationInputModeToggle, InterrogationInputMode } from "./components/InterrogationInputModeToggle";
import { InterrogationExtractionSidebar } from "./components/InterrogationExtractionSidebar";
import { InterrogationTranscriptView } from "./components/InterrogationTranscriptView";
import { KnowledgeGraphPanel } from "./components/KnowledgeGraphPanel";

// Keyframe for content fade in animation
const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

interface LocationState {
    goal?: string;
    sessionId?: string;
    plan?: PlanItem[];
    status?: string;
    mode?: 'guided' | 'interrogation';
    message?: string; // Clarification markdown fallback
    messages?: Array<{ sender: string; message: string; timestamp: string; attachments?: string[] }>;
    isRestoredSession?: boolean;
    clarification_spec?: ClarificationField[];
    execution_started_at?: string;
    // Legacy support
    initialQuestion?: string;
}

function ChatPage() {
    const theme = useTheme();
    const location = useLocation();
    const auth = useAuth();
    const appContext = useContext(AppContext);
    const state = location.state as LocationState | null;
    const messagesEndRef = useRef<HTMLDivElement>(null);

    const [goal, setGoal] = useState<string>("");
    const [sessionId, setSessionId] = useState<string | null>(null);
    const [sessionMode, setSessionMode] = useState<'guided' | 'interrogation'>('guided');
    const [sessionPlan, setSessionPlan] = useState<PlanItemObject[]>([]);
    const [inputValue, setInputValue] = useState<string>("");
    const [contentVisible, setContentVisible] = useState<boolean>(false);
    
    // Detect if screen is in portrait mode (taller than wide)
    const [isPortrait, setIsPortrait] = useState<boolean>(
        typeof window !== 'undefined' ? window.innerHeight > window.innerWidth : false
    );
    
    // Clarification phase state
    const [isInClarificationPhase, setIsInClarificationPhase] = useState<boolean>(false);
    const [isPollingForPlan, setIsPollingForPlan] = useState<boolean>(false);
    const [clarificationMessages, setClarificationMessages] = useState<SessionChatMessage[]>([]);
    const [clarificationSpec, setClarificationSpec] = useState<ClarificationField[] | null>(null);
    const [executionStartedAt, setExecutionStartedAt] = useState<string | undefined>(undefined);
    const [grounding, setGrounding] = useState<GroundingRecord | null>(null);
    const [showProceedDialog, setShowProceedDialog] = useState<boolean>(false);
    const [clarificationFieldErrors, setClarificationFieldErrors] = useState<Record<string, { problem: string; suggestion?: string }>>({});
    const [draftPlan, setDraftPlan] = useState<PlanItem[] | null>(null);
    const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
    const interrogationPollRef = useRef<NodeJS.Timeout | null>(null);
    const isInterrogationMode = sessionMode === 'interrogation';
    const [interrogationMemory, setInterrogationMemory] = useState<InterrogationMemory | undefined>(undefined);
    const [transcriptTail, setTranscriptTail] = useState<TranscriptSegment[]>([]);
    const [speakerRegistry, setSpeakerRegistry] = useState<Record<string, string>>({});
    const [openThreadsCount, setOpenThreadsCount] = useState(0);
    const [interrogationInputMode, setInterrogationInputMode] = useState<InterrogationInputMode>('chat');
    const [hasStartedLiveRecording, setHasStartedLiveRecording] = useState(false);
    const [graphPanelOpen, setGraphPanelOpen] = useState(false);
    const [llmUsage, setLlmUsage] = useState<LlmUsageSummary | undefined>(undefined);
    const [consolidationStatus, setConsolidationStatus] = useState<
        'none' | 'pending' | 'running' | 'complete' | 'failed'
    >('none');
    const [highlightedSourceTimestamp, setHighlightedSourceTimestamp] = useState<string | null>(null);
    const chatMainRef = useRef<HTMLDivElement | null>(null);

    const normalizePlanObjects = (plan?: PlanItem[] | null): PlanItemObject[] => {
        if (!plan || plan.length === 0) return [];
        return plan.map((item, index) => {
            if (typeof item === 'string') {
                return { id: index, task: item, status: 'PENDING' };
            }
            return {
                id: item.id ?? index,
                task: item.task ?? `Step ${index + 1}`,
                status: (item as PlanItemObject).status ?? 'PENDING',
                started_at: (item as PlanItemObject).started_at,
                completed_at: (item as PlanItemObject).completed_at,
                duration_seconds: (item as PlanItemObject).duration_seconds,
            };
        });
    };

    // Convert backend messages to ChatMessage format and auto-inject plan placeholder
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const convertMessages = React.useCallback((messages?: Array<any>): SessionChatMessage[] => {
        if (!messages) return [];
        
        const converted: SessionChatMessage[] = messages.map((msg, index) => {
            const isKnownIssue = msg.message_type === 'KNOWN_ISSUE';
            const isTranscript = msg.sender === 'TRANSCRIPT' || msg.message_type === 'TRANSCRIPT';
            return {
                id: `restored-${index}-${msg.timestamp}`,
                text: msg.message || '',
                isBot: msg.sender === 'SYSTEM',
                timestamp: new Date(msg.timestamp),
                signedAttachments: msg.attachments,
                planPlaceholder: msg.plan_placeholder === true,
                messageType: isTranscript ? 'transcript' : isKnownIssue ? 'known_issue' : undefined,
                sourceTimestamp: msg.timestamp,
            };
        });

        // If the backend didn't supply a placeholder, we inject one so the plan doesn't sink to the bottom.
        if (converted.length > 0 && !converted.some(m => m.planPlaceholder)) {
            // Look for the drafting conclusion message ("I have generated a plan")
            const introIndex = converted.findIndex(m => 
                m.isBot && (
                    m.text.toLowerCase().includes('generated a plan') || 
                    m.text.toLowerCase().includes('review the steps') ||
                    m.text.toLowerCase().includes("here's my plan")
                )
            );

            if (introIndex !== -1) {
                converted.splice(introIndex + 1, 0, {
                    id: `synthetic-plan-placeholder`,
                    text: '',
                    isBot: true,
                    timestamp: new Date(),
                    planPlaceholder: true
                });
                return converted;
            }

            // If no intro found, look for the first execution step "Step 0:", ...)
            const firstStepIndex = converted.findIndex(m => 
                m.isBot && /step \d+:/i.test(m.text)
            );
            
            if (firstStepIndex !== -1) {
                converted.splice(firstStepIndex, 0, {
                    id: `synthetic-plan-placeholder`,
                    text: '',
                    isBot: true,
                    timestamp: new Date(),
                    planPlaceholder: true
                });
                return converted;
            }

            converted.push({
                id: `synthetic-plan-placeholder`,
                text: '',
                isBot: true,
                timestamp: new Date(),
                planPlaceholder: true
            });
        }

        return converted;
    }, []);

    // Prepare initial messages for restored sessions. If the session is
    // already COMPLETED, append the report CTA marker so the user sees the
    // "View Report" card straight away instead of the legacy free-text
    // final message bubble. We default `reportIsCustomerTemplate` to
    // false here because restoration state doesn't carry the flag — the
    // CTA falls back to generic "Session report" copy. The actual report
    // page fetches the structured payload directly and renders the right
    // sections regardless, so the user-facing document is still correct.
    const initialMessages = (() => {
        if (!state?.isRestoredSession) return undefined;
        const base = convertMessages(state.messages);
        if (state.status && isSessionComplete(state.status) && state.sessionId &&
            !base.some(m => m.messageType === 'report_cta')) {
            base.push({
                id: `restored-report-cta-${state.sessionId}`,
                text: 'Your session report is ready.',
                isBot: true,
                timestamp: new Date(),
                messageType: 'report_cta',
                reportSessionId: state.sessionId,
                reportIsCustomerTemplate: false,
            });
        }
        return base;
    })();

    // Keep portrait state in sync with window orientation. Layout decisions
    // that used to depend on a user-togglable sidebar are now phase-driven
    // (see the `phase` computation + PlanSidebar at the render root), so
    // there's nothing else to coordinate on resize.
    useEffect(() => {
        const handleResize = () => {
            setIsPortrait(window.innerHeight > window.innerWidth);
        };
        window.addEventListener('resize', handleResize);
        handleResize();
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Session polling hook
    const {
        executionPhase,
        setExecutionPhase,
        sessionResponse,
        errorMessage,
        setErrorMessage,
        chatMessages,
        setChatMessages,
        currentStep,
        totalSteps,
        startPolling,
        sendReply,
    } = useSessionPolling({
        sessionId,
        auth,
        initialMessages,
    });

    const appendInterrogationReportCta = React.useCallback((response: { report_available?: boolean; report_is_customer_template?: boolean; report_kind?: string }) => {
        if (!sessionId || !response.report_available) return;
        const isCustomer = response.report_is_customer_template === true;
        const kind = (response.report_kind || '').trim();
        const text = isCustomer && kind
            ? `Your ${kind} documentation is ready.`
            : isCustomer
                ? 'Your documentation is ready.'
                : 'Your expert interrogation report is ready.';
        setChatMessages((prev) => {
            if (prev.some((m) => m.messageType === 'report_cta')) return prev;
            return [
                ...prev,
                {
                    id: `bot-report-cta-${Date.now()}`,
                    text,
                    isBot: true,
                    timestamp: new Date(),
                    messageType: 'report_cta',
                    reportSessionId: sessionId,
                    reportIsCustomerTemplate: isCustomer,
                    reportKind: kind || undefined,
                },
            ];
        });
    }, [sessionId, setChatMessages]);

    const syncInterrogationStatus = React.useCallback(async () => {
        if (!sessionId || !isInterrogationMode) return;
        try {
            const response = await getSessionStatus(sessionId, auth);
            if (response.interrogation_memory) {
                setInterrogationMemory(response.interrogation_memory);
            }
            if (response.live_transcript_tail) {
                setTranscriptTail(response.live_transcript_tail);
            }
            if (response.speaker_registry) {
                setSpeakerRegistry(response.speaker_registry);
            }
            if (typeof response.open_threads_count === 'number') {
                setOpenThreadsCount(response.open_threads_count);
            }
            if (response.llm_usage) {
                setLlmUsage(response.llm_usage);
            }
            if (response.consolidation_status) {
                setConsolidationStatus(response.consolidation_status);
            } else {
                setConsolidationStatus('none');
            }
            if (response.messages && response.messages.length > 0) {
                const converted = convertMessages(response.messages);
                setChatMessages(converted);
            }
            if (isSessionComplete(response.status)) {
                setExecutionPhase('completed');
                appendInterrogationReportCta(response);
            } else if (response.status === 'ERROR') {
                setExecutionPhase('error');
            } else if (response.status === 'LISTENING' || response.status === 'NEEDS_INPUT') {
                setExecutionPhase('awaiting_input');
            }
        } catch {
            // polling errors are non-fatal for live transcription UI
        }
    }, [auth, appendInterrogationReportCta, convertMessages, isInterrogationMode, sessionId, setChatMessages, setExecutionPhase]);

    const liveTranscription = useLiveTranscription({
        sessionId,
        auth,
        enabled: isInterrogationMode && interrogationInputMode === 'live' && executionPhase !== 'completed',
        onSegmentSubmitted: (response) => {
            void syncInterrogationStatus();
            if (response.question_issued && response.current_question) {
                setChatMessages((prev) => {
                    if (prev.some((m) => m.isBot && m.text === response.current_question)) {
                        return prev;
                    }
                    return [
                        ...prev,
                        {
                            id: `bot-q-${Date.now()}`,
                            text: response.current_question || '',
                            isBot: true,
                            timestamp: new Date(),
                        },
                    ];
                });
            }
            if (isSessionComplete(response.status)) {
                setExecutionPhase('completed');
                appendInterrogationReportCta({ report_available: true });
            }
        },
        onError: (message) => setErrorMessage(message),
    });

    const knownSpeakerLabels = useMemo(() => {
        const labels = new Set<string>();
        Object.keys(speakerRegistry).forEach((label) => labels.add(label));
        transcriptTail.forEach((segment) => {
            if (segment.speaker_label) labels.add(segment.speaker_label);
        });
        liveTranscription.liveLines.forEach((line) => {
            if (line.speakerLabel) labels.add(line.speakerLabel);
        });
        if (liveTranscription.activeSpeakerLabel) {
            labels.add(liveTranscription.activeSpeakerLabel);
        }
        return Array.from(labels).filter((label) => label && label !== 'unknown');
    }, [
        speakerRegistry,
        transcriptTail,
        liveTranscription.liveLines,
        liveTranscription.activeSpeakerLabel,
    ]);

    const handleSpeakerRegistryChange = React.useCallback(async (nextRegistry: Record<string, string>) => {
        setSpeakerRegistry(nextRegistry);
        if (!sessionId) return;
        try {
            await updateSpeakerRegistry(sessionId, nextRegistry, auth);
        } catch (err) {
            setErrorMessage(err instanceof Error ? err.message : 'Failed to save speaker names');
        }
    }, [auth, sessionId, setErrorMessage]);

    useEffect(() => {
        if (executionPhase === 'completed' || interrogationInputMode !== 'live') {
            void liveTranscription.stopListening();
        }
        // `liveTranscription` is a fresh object every render (useLiveTranscription
        // isn't memoized as a whole); depending on it wholesale would re-run this
        // effect on every render. `stopListening` itself is a stable useCallback
        // and is idempotent when already stopped, so the narrower dependency below
        // is intentional and exhaustive for this effect's actual behavior.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [executionPhase, interrogationInputMode, liveTranscription.stopListening]);

    const handleInterrogationInputModeChange = (mode: InterrogationInputMode) => {
        if (mode === 'chat' && liveTranscription.isListening) {
            void liveTranscription.stopListening();
        }
        setInterrogationInputMode(mode);
    };

    const handleInterrogationFinish = () => {
        if (interrogationInputMode === 'live') {
            void liveTranscription.finishSession();
        } else if (sessionId) {
            void sendReply('finish');
        }
    };

    useEffect(() => {
        if (!sessionId || !isInterrogationMode) return;
        void syncInterrogationStatus();
        interrogationPollRef.current = setInterval(() => {
            void syncInterrogationStatus();
        }, 3000);
        return () => {
            if (interrogationPollRef.current) {
                clearInterval(interrogationPollRef.current);
                interrogationPollRef.current = null;
            }
        };
    }, [isInterrogationMode, sessionId, syncInterrogationStatus]);

    useEffect(() => {
        const newGoal = sessionResponse?.goal;
        if (isInterrogationMode && typeof newGoal === 'string' && newGoal.trim() && newGoal !== goal) {
            setGoal(newGoal);
        }
    }, [sessionResponse, isInterrogationMode, goal]);

    useEffect(() => {
        if (sessionResponse?.llm_usage) {
            setLlmUsage(sessionResponse.llm_usage);
        }
    }, [sessionResponse?.llm_usage]);

    const handleScrollToMessage = React.useCallback((sourceMessageTs: string) => {
        setHighlightedSourceTimestamp(sourceMessageTs);
        const prefix = sourceMessageTs.slice(0, 19);
        const container = chatMainRef.current;
        if (!container) return;
        const nodes = container.querySelectorAll('[data-message-ts]');
        for (const node of Array.from(nodes)) {
            const ts = node.getAttribute('data-message-ts') || '';
            if (ts === sourceMessageTs || ts.startsWith(prefix) || sourceMessageTs.startsWith(ts.slice(0, 19))) {
                node.scrollIntoView({ behavior: 'smooth', block: 'center' });
                break;
            }
        }
        window.setTimeout(() => setHighlightedSourceTimestamp(null), 2500);
    }, []);

    // Plan management hook
    const planManagement = usePlanManagement();

    // Mutation for updating the plan
    const updatePlanMutation = useMutation({
        mutationFn: async (newPlan: string[]) => {
            if (!sessionId) throw new Error("No session ID");
            return updateSessionPlan(sessionId, newPlan, 'APPROVED', auth);
        },
    });

    // Mutation for replying to clarification questions
    const clarificationReplyMutation = useMutation({
        mutationFn: async (text: string) => {
            if (!sessionId) throw new Error("No session ID");
            return replyToSession(sessionId, text, auth);
        },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        onSuccess: (data: any) => {
            const issues = (data && Array.isArray(data.quality_issues)) ? data.quality_issues : [];
            const missing = (data && Array.isArray(data.missing)) ? data.missing : [];
            if (issues.length > 0 || missing.length > 0) {
                const map: Record<string, { problem: string; suggestion?: string }> = {};
                for (const issue of issues) {
                    if (issue && issue.key) {
                        map[issue.key] = { problem: issue.problem || '', suggestion: issue.suggestion };
                    }
                }
                for (const field of missing) {
                    if (typeof field === 'string' && !map[field]) {
                        map[field] = { problem: 'This field is required.' };
                    }
                }
                setClarificationFieldErrors(map);
            } else {
                setClarificationFieldErrors({});
            }
            startPollingForPlan();
        },
        onError: (error: Error) => {
            setErrorMessage(error instanceof Error ? error.message : 'Failed to send reply');
        },
    });

    // Polling function for DRAFTING status. Also detects when the backend's
    // clarification gate bounced our submission back.
    const pollForDraftPlan = React.useCallback(async () => {
        if (!sessionId) return;

        try {
            const response = await getSessionStatus(sessionId, auth);

            if (response.status === 'DRAFTING' && response.plan && response.plan.length > 0) {
                if (pollingIntervalRef.current) {
                    clearInterval(pollingIntervalRef.current);
                    pollingIntervalRef.current = null;
                }
                setIsPollingForPlan(false);
                setDraftPlan(response.plan);
                setSessionPlan(normalizePlanObjects(response.plan));
                if (response.execution_started_at) {
                    setExecutionStartedAt(response.execution_started_at);
                }
                if (response.grounding) {
                    setGrounding(response.grounding);
                }
                setClarificationFieldErrors({});

                // Sync messages from the backend so the chat history persists
                if (response.messages && response.messages.length > 0) {
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    setChatMessages(convertMessages(response.messages as any));
                }

                setIsInClarificationPhase(false);
                setExecutionPhase('editing');
                return;
            }

            if (response.status === 'CLARIFICATION_NEEDED') {
                if (pollingIntervalRef.current) {
                    clearInterval(pollingIntervalRef.current);
                    pollingIntervalRef.current = null;
                }
                setIsPollingForPlan(false);
                setIsInClarificationPhase(true);
                if (response.messages && response.messages.length > 0) {
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    setClarificationMessages(convertMessages(response.messages as any));
                }
                if (response.clarification_spec && response.clarification_spec.length > 0) {
                    setClarificationSpec(response.clarification_spec);
                }
            }
        } catch (err) {
            setErrorMessage(err instanceof Error ? err.message : "Failed to get session status");
            if (pollingIntervalRef.current) {
                clearInterval(pollingIntervalRef.current);
                pollingIntervalRef.current = null;
            }
            setIsPollingForPlan(false);
        }
    }, [sessionId, auth, setExecutionPhase, setErrorMessage, setChatMessages, convertMessages]);

    // Start polling for draft plan
    const startPollingForPlan = React.useCallback(() => {
        if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
        }
        
        setIsPollingForPlan(true);
        
        // Initial poll
        pollForDraftPlan();
        
        // Set up polling interval (every 2 seconds)
        pollingIntervalRef.current = setInterval(pollForDraftPlan, 2000);
    }, [pollForDraftPlan]);

    // Initialize plan when draft plan is received
    React.useEffect(() => {
        if (draftPlan && draftPlan.length > 0 && planManagement.planSteps.length === 0) {
            console.log('[EFFECT] Initializing plan from draft with', draftPlan.length, 'steps');
            console.log('[EFFECT] Current state:', {
                clarificationMessagesCount: clarificationMessages.length,
                chatMessagesCount: chatMessages.length,
            });
            planManagement.initializePlan(draftPlan);
            // Clear the draft plan so we don't re-initialize
            setDraftPlan(null);
        }
    }, [draftPlan, planManagement, clarificationMessages.length, chatMessages.length]);

    // Cleanup polling on unmount
    React.useEffect(() => {
        return () => {
            if (pollingIntervalRef.current) {
                clearInterval(pollingIntervalRef.current);
            }
        };
    }, []);

    // Keep sessionPlan fresh during execution + completion (guided sessions only).
    React.useEffect(() => {
        if (!sessionId || isInterrogationMode) return;
        const shouldRefresh =
            executionPhase === 'polling' ||
            executionPhase === 'awaiting_input' ||
            executionPhase === 'submitting_reply' ||
            executionPhase === 'starting' ||
            executionPhase === 'completed';
        if (!shouldRefresh) return;

        let cancelled = false;
        const refresh = async () => {
            try {
                const response = await getSessionStatus(sessionId!, auth);
                if (cancelled) return;
                if (response.plan && Array.isArray(response.plan)) {
                    setSessionPlan(response.plan as PlanItemObject[]);
                }
                if (response.execution_started_at) {
                    setExecutionStartedAt(response.execution_started_at);
                }
                if (response.status === 'COMPLETED' &&
                    typeof response.report_is_customer_template === 'boolean') {
                    const isCustomer = response.report_is_customer_template;
                    const kind = (response.report_kind || '').trim();
                    const text = isCustomer && kind
                        ? `Your ${kind} documentation is ready.`
                        : isCustomer
                            ? 'Your documentation is ready.'
                            : 'Your session report is ready.';
                    setChatMessages((prev: SessionChatMessage[]) => {
                        let changed = false;
                        const next = prev.map((m: SessionChatMessage) => {
                            if (m.messageType === 'report_cta' &&
                                (m.reportIsCustomerTemplate !== isCustomer ||
                                 (m.reportKind || '') !== kind)) {
                                changed = true;
                                return {
                                    ...m,
                                    text,
                                    reportIsCustomerTemplate: isCustomer,
                                    reportKind: kind || undefined,
                                };
                            }
                            return m;
                        });
                        return changed ? next : prev;
                    });
                }
            } catch {
                // Silent — the primary useSessionPolling will surface errors.
            }
        };
        refresh();
        if (executionPhase === 'completed') return;
        const id = setInterval(refresh, 3000);
        return () => {
            cancelled = true;
            clearInterval(id);
        };
    }, [sessionId, auth, executionPhase, isInterrogationMode, setChatMessages]);

    // Scroll to bottom when chat messages change
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [chatMessages, executionPhase]);

    // Ensure navbar is visible on ChatPage (as a safety check)
    useEffect(() => {
        if (appContext && appContext.isLandingMode) {
            appContext.setIsLandingMode(false);
        }
    }, [appContext]);

    // Reveal content after menu bar animation completes
    useEffect(() => {
        if (appContext?.menuBarAnimationComplete) {
            // Small delay after menu bar animation for smoother transition
            const timer = setTimeout(() => {
                setContentVisible(true);
            }, 50);
            return () => clearTimeout(timer);
        } else {
            // If already not in landing mode (e.g., direct navigation), show immediately
            if (appContext && !appContext.isLandingMode) {
                setContentVisible(true);
            }
        }
    }, [appContext]);

    // Initialize from navigation state (Runs strictly once)
    const hasInitializedFromState = useRef(false);

    useEffect(() => {
        if (!state || hasInitializedFromState.current) return;
        
        if (state.goal && !goal) setGoal(state.goal);
        if (state.sessionId && !sessionId) setSessionId(state.sessionId);
        if (state.execution_started_at) setExecutionStartedAt(state.execution_started_at);
        if (state.mode === 'interrogation') setSessionMode('interrogation');
        
        // Handle clarification message from initial session creation
        if (state.message && state.mode === 'interrogation') {
            const baseMessages: SessionChatMessage[] = [];
            if (state.goal) {
                baseMessages.push({
                    id: `user-goal-${Date.now()}`,
                    text: state.goal,
                    isBot: false,
                    timestamp: new Date(),
                });
            }
            baseMessages.push({
                id: `bot-open-${Date.now() + 1}`,
                text: state.message,
                isBot: true,
                timestamp: new Date(),
            });
            setChatMessages(baseMessages);
            setExecutionPhase('awaiting_input');
        } else if (state.message && state.status === 'CLARIFICATION_NEEDED') {
            setClarificationMessages([
                { id: `user-${Date.now()}`, text: state.goal || '', isBot: false, timestamp: new Date() },
                { id: `bot-${Date.now() + 1}`, text: state.message, isBot: true, timestamp: new Date() }
            ]);
            if (state.clarification_spec && state.clarification_spec.length > 0) {
                setClarificationSpec(state.clarification_spec);
            }
            setIsInClarificationPhase(true);
            setExecutionPhase('awaiting_input');
        } else if (state.plan && state.plan.length > 0 && planManagement.planSteps.length === 0) {
            planManagement.initializePlan(state.plan);
            setSessionPlan(normalizePlanObjects(state.plan));
        }
        
        // Legacy support
        if (state.initialQuestion && !state.goal && !goal) setGoal(state.initialQuestion);

        // Handle restored session
        if (state.isRestoredSession && state.status) {
            const status = state.status.toUpperCase();
            if (state.mode === 'interrogation') {
                if (isSessionComplete(status)) setExecutionPhase('completed');
                else if (status === 'ERROR') {
                    setExecutionPhase('error');
                    setErrorMessage('This session encountered an error');
                } else {
                    setExecutionPhase('awaiting_input');
                }
            } else if (status === 'RUNNING') setTimeout(() => startPolling(), 100);
            else if (status === 'NEEDS_INPUT') setExecutionPhase('awaiting_input');
            else if (status === 'COMPLETED') setExecutionPhase('completed');
            else if (status === 'ERROR') {
                setExecutionPhase('error');
                setErrorMessage('This session encountered an error');
            }
        }
        
        // Handle new session from Home
        if (!state.isRestoredSession && state.status && !state.message) {
            const status = state.status.toUpperCase();
            if (state.mode === 'interrogation') {
                setExecutionPhase(isSessionComplete(status) ? 'completed' : 'awaiting_input');
            } else if (status === 'PLANNING') {
                setExecutionPhase('editing');
                startPollingForPlan();
            } else if (status === 'DRAFTING') {
                setExecutionPhase('editing');
            }
        }

        hasInitializedFromState.current = true;
    }, [state, goal, sessionId, planManagement, startPolling, setExecutionPhase, setErrorMessage, startPollingForPlan, setChatMessages]);

    // Handle edit keydown
    const handleEditKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            planManagement.saveEdit();
        }
        if (e.key === "Escape") {
            e.preventDefault();
            planManagement.cancelEdit();
        }
    };

    // Actual "start" work. Split out so the grounding-confirmation dialog can
    // call it after the user acknowledges proceeding with an LLM-only plan.
    const doStartExecution = async () => {
        if (!sessionId || updatePlanMutation.isPending) return;
        setExecutionPhase('starting');
        setErrorMessage(null);
        try {
            const newPlan = planManagement.getFinalPlan();
            planManagement.commitChanges();
            await updatePlanMutation.mutateAsync(newPlan);

            // Sync sessionPlan to the user's edited plan. Without this, the
            // PlanSidebar keeps showing the ORIGINAL pre-edit plan — e.g. if
            // the user deleted 5 of 6 steps, the sidebar would still show
            // all 6 while execution runs against just 1.
            setSessionPlan(
                newPlan.map((task: string, i: number) => ({
                    id: i,
                    task,
                    status: 'PENDING',
                }))
            );

            await startSession(sessionId, auth);
            startPolling();
        } catch (error) {
            setErrorMessage(error instanceof Error ? error.message : 'Unknown error');
            setExecutionPhase('error');
        }
    };

    // Entry point for the "Submit Changes and Start Plan" button. When
    // grounding is partial or none, we gate the start behind an explicit
    // confirmation dialog — the user shouldn't be able to blow through this
    // with a habitual click.
    const handleSubmitAndStart = async () => {
        if (!sessionId || updatePlanMutation.isPending || executionPhase !== 'editing') return;
        if (grounding && grounding.status !== 'ok') {
            setShowProceedDialog(true);
            return;
        }
        await doStartExecution();
    };

    // Handler for the structured ClarificationForm: serializes the selections
    // into markdown (the backend's clarification gate + plan builder both
    // consume free text, so this preserves full compatibility).
    const handleClarificationFormSubmit = (markdown: string, _structured: Record<string, string | string[]>) => {
        if (!sessionId || clarificationReplyMutation.isPending || isPollingForPlan) return;
        setClarificationMessages((prev: SessionChatMessage[]) => [
            ...prev,
            {
                id: `user-form-${Date.now()}`,
                text: markdown,
                isBot: false,
                timestamp: new Date(),
            },
        ]);
        clarificationReplyMutation.mutate(markdown);
    };

    // Handle sending a reply to the bot's question
    const handleSendReply = async (attachmentKeysOrMessage?: string[] | string, localFiles?: File[]) => {
        console.log('[ChatPage] handleSendReply called with:', attachmentKeysOrMessage, 'Type:', typeof attachmentKeysOrMessage, 'Files:', localFiles);
        
        // If it's a string, use it directly (for button clicks)
        if (typeof attachmentKeysOrMessage === 'string') {
            console.log('[ChatPage] Processing as string message:', attachmentKeysOrMessage);
            if (!attachmentKeysOrMessage.trim()) return;
            
            // Check if we're in clarification phase
            if (isInClarificationPhase) {
                // Add user message to chat
                setClarificationMessages((prev: SessionChatMessage[]) => [...prev, {
                    id: `user-${Date.now()}`,
                    text: attachmentKeysOrMessage.trim(),
                    isBot: false,
                    timestamp: new Date(),
                }]);
                clarificationReplyMutation.mutate(attachmentKeysOrMessage.trim());
            } else {
                await sendReply(attachmentKeysOrMessage.trim());
            }
            return;
        }
        
        // Otherwise, it's attachment keys from ChatInput (after file upload)
        console.log('[ChatPage] Processing attachment keys with inputValue:', inputValue);
        const attachmentKeys = attachmentKeysOrMessage as string[] | undefined;
        
        const hasText = inputValue.trim().length > 0;
        const hasAttachments = attachmentKeys && attachmentKeys.length > 0;

        // If neither text nor attachments exist, stop here
        if (!hasText && !hasAttachments) return;

        // Apply fallback text if only an attachment is sent
        let userAnswer = inputValue.trim();
        if (!hasText && hasAttachments) {
            userAnswer = "Look at the image provided";
        }
        
        setInputValue("");
        
        // Check if we're in clarification phase
        if (isInClarificationPhase) {
            // Add user message to chat
            setClarificationMessages((prev: SessionChatMessage[]) => [...prev, {
                id: `user-${Date.now()}`,
                text: userAnswer,
                isBot: false,
                timestamp: new Date(),
            }]);
            clarificationReplyMutation.mutate(userAnswer);
        } else {
            await sendReply(userAnswer, attachmentKeys, localFiles);
        }
    };

    // Check if editing is allowed (only in editing phase)
    const canEdit = !isInterrogationMode && executionPhase === 'editing';
    const isLoading = updatePlanMutation.isPending || executionPhase === 'starting';
    
    // Check if the last message has action buttons (completion question)
    const lastMessage = chatMessages.length > 0 ? chatMessages[chatMessages.length - 1] : null;
    const hasActionButtons = !isInterrogationMode && lastMessage?.isBot && 
        lastMessage.text.toLowerCase().includes('all execution steps are complete') && 
        lastMessage.text.toLowerCase().includes('what would you like to do');


    // Render clarification chat content
    const renderClarificationContent = () => (
        <>
            {/* Show clarification messages if we have any (from clarification phase) */}
            {clarificationMessages.length > 0 && (
                <ChatMessages 
                    messages={clarificationMessages} 
                    onSendReply={handleSendReply} 
                />
            )}
        </>
    );

    // Render messages with plan embedded at the correct position
    const renderMessagesWithPlan = () => {
        if (chatMessages.length === 0) return null;

        const visibleMessages = isInterrogationMode
            ? chatMessages.filter((m) => m.messageType !== 'transcript')
            : chatMessages;
        if (visibleMessages.length === 0) return null;

        const elements: React.ReactNode[] = [];
        let currentBatch: SessionChatMessage[] = [];
        
        const flushBatch = () => {
            if (currentBatch.length > 0) {
                elements.push(
                    <ChatMessages 
                        key={`batch-${currentBatch[0].id}`}
                        messages={currentBatch} 
                        onSendReply={handleSendReply}
                        highlightedSourceTimestamp={highlightedSourceTimestamp}
                    />
                );
                currentBatch = [];
            }
        };
        
        visibleMessages.forEach((msg) => {
            // If this is a plan placeholder, flush current batch and render the plan
            if (msg.planPlaceholder) {
                flushBatch();
                
                if (planManagement.planSteps.length > 0) {
                    elements.push(
                        <Box
                            key={`plan-${msg.id}`}
                            sx={{
                                alignSelf: 'flex-start',
                                maxWidth: '85%',
                            }}
                        >
                            <PlanSteps
                                planSteps={planManagement.planSteps}
                                editingStepIndex={planManagement.editingStepIndex}
                                editText={planManagement.editText}
                                canEdit={canEdit}
                                commandPrefix={planManagement.commandPrefix}
                                onEditTextChange={planManagement.setEditText}
                                onStartEditing={planManagement.startEditingStep}
                                onSaveEdit={planManagement.saveEdit}
                                onCancelEdit={planManagement.cancelEdit}
                                onToggleStep={planManagement.toggleStep}
                                onRevertStep={planManagement.revertStepToOriginal}
                                onResetAll={planManagement.resetAllChanges}
                                onAddStep={planManagement.addStep}
                                onEditKeyDown={handleEditKeyDown}
                            />
                        </Box>
                    );
                }
            } else {
                // Add to current batch of regular messages
                currentBatch.push(msg);
            }
        });
        
        // Flush any remaining messages
        flushBatch();

        return <>{elements}</>;
    };

    const phase: SessionPhase = (() => {
        if (executionPhase === 'error') return 'error';
        if (isInClarificationPhase) return 'clarification';
        if (isPollingForPlan) return 'drafting';
        if (executionPhase === 'completed') return 'completion';
        if (executionPhase === 'editing') return 'review';
        if (executionPhase === 'starting') return 'starting';
        return 'execution';
    })();

    const showPlanSidebar =
        !isInterrogationMode &&
        (phase === 'execution' || phase === 'completion') &&
        sessionPlan.length > 0 &&
        !isPortrait;

    const showExtractionSidebar =
        isInterrogationMode &&
        executionPhase !== 'completed' &&
        !isPortrait;

    return (
        <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            minHeight: 0,
            overflow: 'hidden',
        }}>
            <SessionHeader
                goal={goal}
                sessionId={sessionId}
                phase={phase}
                progress={{ done: currentStep ? Math.max(0, currentStep - 1) : 0, total: totalSteps || planManagement.planSteps.length }}
                grounding={grounding}
                llmUsage={llmUsage}
                onScrollToMessage={handleScrollToMessage}
            />

            <Box sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'row',
                overflow: 'hidden',
                minHeight: 0,
            }}>
                {showPlanSidebar && (
                    <PlanSidebar
                        plan={sessionPlan}
                        currentStep={currentStep}
                        executionStartedAt={executionStartedAt}
                    />
                )}

                <Box sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                    minHeight: 0,
                    opacity: contentVisible ? 1 : 0,
                    animation: contentVisible ? `${fadeIn} 0.4s ease-out forwards` : 'none',
                }}>
                    <Box
                        component="main"
                        ref={chatMainRef}
                        sx={{
                            flex: 1,
                            overflowY: 'auto',
                            overflowX: 'hidden',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 2,
                            px: 2,
                            py: 2,
                            minHeight: 0,
                            '@media (min-width: 900px)': {
                                px: showPlanSidebar ? 2 : 3,
                                py: 2.5,
                            },
                            ...(!showPlanSidebar && {
                                alignItems: 'center',
                            }),
                        }}
                    >
                        <Box sx={{
                            width: '100%',
                            maxWidth: showPlanSidebar ? 'none' : '960px',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 2,
                        }}>
                            {(() => {
                                console.log('[RENDER] Order check:', {
                                    clarificationMessagesCount: clarificationMessages.length,
                                    planStepsCount: planManagement.planSteps.length,
                                    chatMessagesCount: chatMessages.length,
                                    isInClarificationPhase,
                                    executionPhase,
                                });
                                return null;
                            })()}
                            
                            {/* CLARIFICATION PHASE */}
                            {isInClarificationPhase && (
                                <>
                                    {/* Clarification messages */}
                                    {renderClarificationContent()}

                                    {clarificationSpec && clarificationSpec.length > 0 && (
                                        <Box sx={{ alignSelf: 'stretch' }}>
                                            <ClarificationForm
                                                spec={clarificationSpec}
                                                onSubmit={handleClarificationFormSubmit}
                                                disabled={clarificationReplyMutation.isPending || isPollingForPlan}
                                                fieldErrors={clarificationFieldErrors}
                                            />
                                        </Box>
                                    )}

                                    {/* Typing indicator during clarification reply */}
                                    {clarificationReplyMutation.isPending && (
                                        <TypingIndicator />
                                    )}

                                    {/* Error display during clarification */}
                                    {errorMessage && (
                                        <CollapsibleMessageBox
                                            title="Error"
                                            message={errorMessage}
                                            variant="error"
                                        />
                                    )}
                                </>
                            )}

                            {/* PLAN CREATION LOADING */}
                            {isPollingForPlan && (
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        gap: 1.5,
                                        p: 3,
                                        borderRadius: "12px",
                                        bgcolor: alpha(theme.palette.background.paper, 0.8),
                                        border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                                    }}
                                >
                                    <CircularProgress size={20} />
                                    <Typography variant="body2" color="text.secondary">
                                        Creating your plan...
                                    </Typography>
                                </Box>
                            )}

                            {/* EXECUTION PHASE */}
                            {!isInClarificationPhase && !isPollingForPlan && (
                                <>
                                    {/* Grounding status now lives entirely in SessionHeader
                                        (chip + collapsible warning strip). No in-body banner
                                        or badge — the header is the one source of truth. */}

                                    {/* Session timeline: per-step durations + session-wide total.
                                        Hides itself until at least one step has completed, so early
                                        editing / planning doesn't show an empty card. */}
                                    {sessionPlan.length > 0 && (
                                        <SessionDurationsCard
                                            plan={sessionPlan}
                                            sessionStartedAt={executionStartedAt}
                                        />
                                    )}

                                    {isInterrogationMode && interrogationInputMode === 'live' && executionPhase !== 'completed' && (
                                        <InterrogationTranscriptView
                                            persistedSegments={transcriptTail}
                                            liveLines={liveTranscription.liveLines}
                                            partialText={liveTranscription.partialText}
                                            partialSpeakerLabel={liveTranscription.activeSpeakerLabel}
                                            speakerRegistry={speakerRegistry}
                                            isRecording={liveTranscription.isListening}
                                            isConnecting={liveTranscription.isConnecting}
                                            isReconnecting={liveTranscription.isReconnecting}
                                            awaitingStart={!hasStartedLiveRecording && !liveTranscription.isListening}
                                        />
                                    )}

                                    {/* Messages with plan embedded at correct position (for restored sessions) */}
                                    {renderMessagesWithPlan()}

                                    {/* Fallback Plan: Render standalone if no placeholder exists in messages */}
                                    {!isInterrogationMode && !chatMessages.some(m => m.planPlaceholder) && planManagement.planSteps.length > 0 && (
                                        <Box
                                            sx={{
                                                alignSelf: 'flex-start',
                                                maxWidth: '85%',
                                                mt: chatMessages.length > 0 ? 2 : 0
                                            }}
                                        >
                                            <PlanSteps
                                                planSteps={planManagement.planSteps}
                                                editingStepIndex={planManagement.editingStepIndex}
                                                editText={planManagement.editText}
                                                canEdit={canEdit}
                                                commandPrefix={planManagement.commandPrefix}
                                                onEditTextChange={planManagement.setEditText}
                                                onStartEditing={planManagement.startEditingStep}
                                                onSaveEdit={planManagement.saveEdit}
                                                onCancelEdit={planManagement.cancelEdit}
                                                onToggleStep={planManagement.toggleStep}
                                                onRevertStep={planManagement.revertStepToOriginal}
                                                onResetAll={planManagement.resetAllChanges}
                                                onAddStep={planManagement.addStep}
                                                onEditKeyDown={handleEditKeyDown}
                                            />
                                        </Box>
                                    )}

                                    {/* Typing indicator when processing execution */}
                                    {(executionPhase === 'polling' || executionPhase === 'submitting_reply') && (
                                        <TypingIndicator />
                                    )}

                                    {/* Error display during execution */}
                                    {errorMessage && (
                                        <CollapsibleMessageBox
                                            title="Error"
                                            message={errorMessage}
                                            variant="error"
                                        />
                                    )}
                                </>
                            )}

                            {/* Scroll anchor at the very end */}
                            <div ref={messagesEndRef} />
                        </Box>
                    </Box>

                    {/* Bottom Bar: Submit Button or Chat Input - Fixed at bottom within main content area */}
                    {executionPhase === 'editing' && !isInClarificationPhase && !isInterrogationMode && (
                        <Box
                            sx={{
                                borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                                bgcolor: theme.palette.background.paper,
                                px: 2,
                                pt: 2, 
                                pb: 'max(16px, env(safe-area-inset-bottom, 16px))', 
                                flexShrink: 0,
                                '@media (min-width: 600px)': {
                                    px: 3,
                                    pt: 2.5,
                                    pb: 2.5,
                                },
                            }}
                        >
                            <Box sx={{ display: 'flex', justifyContent: 'center', maxWidth: showPlanSidebar ? 'none' : '960px', mx: showPlanSidebar ? 0 : 'auto' }}>
                                <Button
                                    variant="contained"
                                    size="large"
                                    onClick={handleSubmitAndStart}
                                    disabled={!sessionId || isLoading}
                                    startIcon={<PlayArrowIcon />}
                                    sx={{
                                        px: 3, py: 1.5, borderRadius: '12px', fontWeight: 600, textTransform: 'none', fontSize: '0.95rem', width: '100%', maxWidth: '400px',
                                        '@media (min-width: 600px)': { px: 4, fontSize: '1rem', width: 'auto', maxWidth: 'none' },
                                    }}
                                >
                                    Submit Changes and Start Plan
                                </Button>
                            </Box>
                        </Box>
                    )}

                    {((executionPhase === 'awaiting_input' && !isInClarificationPhase) || executionPhase === 'completed') && !isInterrogationMode && (
                        <ChatInput
                            value={inputValue}
                            onChange={setInputValue}
                            onSubmit={handleSendReply}
                            disabled={executionPhase === 'completed' || hasActionButtons}
                            placeholder={
                                executionPhase === 'completed' 
                                    ? "Session completed" 
                                    : hasActionButtons 
                                        ? "Please select an option above"
                                        : "Type your answer..."
                            }
                            sessionId={sessionId || undefined}
                            allowAttachments={executionPhase === 'awaiting_input'} // Allow attachments during execution
                        />
                    )}

                    {isInterrogationMode && !isInClarificationPhase && (
                        <>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    px: 2,
                                    py: 0.75,
                                    borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                                    bgcolor: theme.palette.background.paper,
                                    flexShrink: 0,
                                }}
                            >
                                <Box sx={{ flex: 1 }} />
                                <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={<AccountTreeIcon />}
                                    onClick={() => setGraphPanelOpen(true)}
                                    sx={{ textTransform: 'none', fontWeight: 600 }}
                                >
                                    Knowledge graph
                                </Button>
                            </Box>
                            <InterrogationInputModeToggle
                                mode={interrogationInputMode}
                                onModeChange={handleInterrogationInputModeChange}
                                isCompleted={executionPhase === 'completed'}
                                isListening={liveTranscription.isListening}
                                onFinish={() => { void handleInterrogationFinish(); }}
                            />
                            {interrogationInputMode === 'chat' && executionPhase !== 'completed' && (
                                <ChatInput
                                    value={inputValue}
                                    onChange={setInputValue}
                                    onSubmit={async () => {
                                        await handleSendReply();
                                        void syncInterrogationStatus();
                                    }}
                                    disabled={executionPhase === 'submitting_reply'}
                                    placeholder={
                                        executionPhase === 'submitting_reply'
                                            ? 'Extracting entities — this can take a minute…'
                                            : 'Paste or type your answer, then Enter or Send…'
                                    }
                                    sessionId={sessionId || undefined}
                                    allowAttachments={false}
                                    submitOnEnter
                                />
                            )}
                            {interrogationInputMode === 'live' && executionPhase !== 'completed' && (
                                <InterrogationListenPanel
                                    isListening={liveTranscription.isListening}
                                    isConnecting={liveTranscription.isConnecting}
                                    isCompleted={false}
                                    hasStartedBefore={hasStartedLiveRecording}
                                    onStart={() => {
                                        void liveTranscription.startListening().then((ok) => {
                                            if (ok) setHasStartedLiveRecording(true);
                                        });
                                    }}
                                    onStop={() => { void liveTranscription.stopListening(); }}
                                />
                            )}
                        </>
                    )}

                    {/* Chat input during clarification phase */}
                    {isInClarificationPhase && (
                        <ChatInput
                            value={inputValue}
                            onChange={setInputValue}
                            onSubmit={handleSendReply}
                            disabled={clarificationReplyMutation.isPending || isPollingForPlan}
                            placeholder={isPollingForPlan ? "Creating your plan..." : "Type your answer..."}
                            sessionId={sessionId || undefined}
                            allowAttachments={true} // Changed this to allow attachments during clarification
                        />
                    )}
                </Box>

                {showExtractionSidebar && (
                    <InterrogationExtractionSidebar
                        memory={interrogationMemory}
                        speakerRegistry={speakerRegistry}
                        speakerLabels={knownSpeakerLabels}
                        openThreadsCount={openThreadsCount}
                        onSpeakerRegistryChange={handleSpeakerRegistryChange}
                        isExtracting={executionPhase === 'submitting_reply'}
                    />
                )}
            </Box>

            {/* Grounding-failure confirmation dialog. Shown only when the user
                clicks "Start" on a plan with partial/none grounding — forces an
                explicit ack before the plan executes. */}
            <GroundingProceedDialog
                open={showProceedDialog}
                grounding={grounding}
                onCancel={() => setShowProceedDialog(false)}
                onProceed={() => {
                    setShowProceedDialog(false);
                    doStartExecution();
                }}
            />

            {isInterrogationMode && (
                <KnowledgeGraphPanel
                    open={graphPanelOpen}
                    onClose={() => setGraphPanelOpen(false)}
                    memory={interrogationMemory}
                    consolidationStatus={consolidationStatus}
                />
            )}
        </Box>
    );
}

export default ChatPage;
